import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Button, checkPasswordStrength, PasswordField, PasswordStrength } from 'components/common';
import Head from 'components/common/Head/Head';
import { LoginSignupWrapper } from 'components/styled/Form.styled';
import RegistrationHeader from 'components/styled/RegistrationHeader';
import { useMutationPasswordInvite } from 'modules/account/features/forgotPassword/hooks';
import { ButtonSpacing } from 'modules/account/features/singup/components/index.styled';
import { TPasswordStrength } from 'modules/account/features/singup/models';

export const PasswordInviteSchema = Yup.object().shape({
  password: Yup.string().required('Required'),
  confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], "Passwords don't match"),
});

export const PasswordInvite = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [passwordStrength, setPasswordStrength] = useState<TPasswordStrength[]>([]);

  const { passwordInviteMutate, passwordInviteIsLoading } = useMutationPasswordInvite();

  const resetPasswordSubmitForm = async (values: any) => {
    const isValidPassword = passwordStrength.every((item: TPasswordStrength) => item.isValid);
    if (!isValidPassword) return;

    await passwordInviteMutate({ ...values, token: token as string });
    navigate('/login');
  };

  return (
    <>
      <Head title={t('Reset password')} />
      <RegistrationHeader>{t('Reset password')}</RegistrationHeader>
      <Formik
        initialValues={{
          password: '',
          confirmPassword: '',
        }}
        validationSchema={PasswordInviteSchema}
        validate={(values: any) => {
          const { password } = values;
          const passwordValidation = checkPasswordStrength(password);
          setPasswordStrength(passwordValidation);
        }}
        onSubmit={resetPasswordSubmitForm}
      >
        {({ errors, touched }) => (
          <LoginSignupWrapper>
            <PasswordField
              mb="0.8rem"
              label="New password"
              name="password"
              isRequired
              error={errors['password'] && touched['password']}
            />
            <PasswordStrength conditionValues={passwordStrength} />

            <PasswordField
              mt="1.25rem"
              label="Repeat new password"
              name="confirmPassword"
              isRequired
              error={errors['confirmPassword'] && touched['confirmPassword']}
            />

            <ButtonSpacing>
              <Button
                type="submit"
                label="Create"
                color="blue"
                width="100%"
                isFetching={passwordInviteIsLoading}
              />
            </ButtonSpacing>
          </LoginSignupWrapper>
        )}
      </Formik>
    </>
  );
};
